import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useState, createContext, useContext, useEffect } from "react";
import { Header } from "./components/Header";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { ProfilePage } from "./pages/ProfilePage";
import { OrderConfirmationPage } from "./pages/OrderConfirmationPage";
import {
  BranchDto,
  CartItem,
  GoogleLoginDTO,
  LoginDTO,
  OrderMode,
  Restaurant,
  RestaurantWithBranchesDto,
  SignUpDTO,
} from "./types";
import {
  signUpUser,
  loginUser,
  googleLogin,
  guestLogin,
} from "@/services/userService";
// Stripe
import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import { getStripe } from "./services/stripe";
import { getRestaurantDetailsWithBranches } from "./services/restaurantService";
import apiConfig from "./configs/apiConfig";
import {
  loadCartFromStorage,
  saveCartToStorage,
} from "./services/localStorage";
import ScrollToTop from "./components/ScrollToTop";
import ScrollUpButton from "./components/ScrollUpButton";
import { isTokenExpired } from "./services/utlits";
interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  createdDate?: Date;
}
interface AppContextType {
  user: User | null;
  restaurant: Restaurant | null;
  restaurantWithBranches: RestaurantWithBranchesDto | null;
  isLoggedIn: boolean;
  isDeliveryModeSelected: boolean;
  cartItems: CartItem[];
  orderMode: OrderMode;
  getRestaurantDetails: () => void;
  login: (
    email: string,
    password: string,
    name?: string,
  ) => Promise<Awaited<ReturnType<typeof loginUser>>>;
  register: (email: string, password: string, name?: string) => void;
  loginWithGoogle: (clientResponse: any) => void;
  continueAsGuest: () => void;
  logout: () => void;
  addToCart: (item: CartItem) => void;
  updateCartQuantity: (itemId: string, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  setOrderMode: (mode: OrderMode) => void;
  setDeliveryModeSelected: (mode: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
};

function App() {
  const stripePromise = getStripe();

  const [user, setUser] = useState<User | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [restaurantWithBranches, setRestaurantWithBranches] =
    useState<RestaurantWithBranchesDto | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isDeliveryModeSelected, setDeliveryModeSelected] =
    useState<boolean>(false);
  const [orderMode, setOrderMode] = useState<OrderMode>({
    type: "pickup",
    scheduledTime: "ASAP",
    scheduledDate: new Date().toISOString(),
    location: "The Biryanis",
  });

  const isLoggedIn = !!user;

  const login = async (
    email: string,
    password: string,
    name?: string,
  ): Promise<Awaited<ReturnType<typeof loginUser>>> => {
    const loginDto: LoginDTO = {
      email,
      password,
      restaurantId: apiConfig.RESTAURANT_ID,
    };
    const response = await loginUser(loginDto);

    if (response.success && response.loggedInUser) {
      setUser({
        id: response.loggedInUser.id,
        name: response.loggedInUser.name,
        email: response.loggedInUser.email,
        role: response.loggedInUser.role,
        createdDate: response.loggedInUser.createdDate,
      });
    } else {
      console.error("Login failed:", response.error);
    }
    return response;
  };
  const register = async (email: string, password: string, name?: string) => {
    const registerUser: SignUpDTO = {
      email,
      password,
      fullName: name || email.split("@")[0],
    };

    const response = await signUpUser(registerUser);

    if (response.success && response.loggedInUser) {
      setUser({
        id: response.loggedInUser.id,
        name: response.loggedInUser.name,
        email: response.loggedInUser.email,
        createdDate: response.loggedInUser.createdDate,
      });
    } else {
      console.error("Registration failed:", response.error);
    }
  };
  const loginWithGoogle = async (dto: any) => {
    const response = await googleLogin(dto);

    if (response.success && response.loggedInUser) {
      setUser({
        id: response.loggedInUser.id,
        name: response.loggedInUser.name,
        email: response.loggedInUser.email,
        createdDate: response.loggedInUser.createdDate,
      });
    } else {
      console.error("Google Login failed:", response.error);
    }
  };
  const continueAsGuest = async () => {
    const response = await guestLogin();

    if (response.success && response.loggedInUser) {
      setUser({
        id: response.loggedInUser.id,
        name: response.loggedInUser.name,
        email: response.loggedInUser.email,
        createdDate: response.loggedInUser.createdDate,
      });
    } else {
      console.error("Guest Login failed:", response.error);
    }
  };
  const getRestaurantDetails = async () => {
    const response = await getRestaurantDetailsWithBranches();

    if (response) {
      setRestaurant({
        id: response.id,
        name: response.name,
        email: response.email,
        phone: response.phoneNumber,
        address1: response.addressLane1,
        address2: response.addressLane2,
        payLaterEnabled: response.payLaterEnabled,
        isDeliveryModeEnabled: response.isDeliveryModeEnabled,
        isPickupModeEnabled: response.isPickupModeEnabled,
        taxPercentage: response.taxPercentage,
        branches: response?.branches,
        taxRules: response?.taxRules,
        serviceFee: response.serviceFee,
        isClosed: response.isClosed,
        closureReason: response.closureReason,
      });
      if (
        response.isDeliveryModeEnabled == true &&
        response.isPickupModeEnabled == true
      ) {
        setOrderMode({
          type: "pickup",
          scheduledTime: "ASAP",
          scheduledDate: new Date().toISOString(),
          location: "The Biryanis",
        });
      } else if (response.isDeliveryModeEnabled == true) {
        setOrderMode({
          type: "delivery",
          scheduledTime: "ASAP",
          scheduledDate: new Date().toISOString(),
          location: "The Biryanis",
        });
      } else if (response.isPickupModeEnabled == true) {
        setOrderMode({
          type: "pickup",
          scheduledTime: "ASAP",
          scheduledDate: new Date().toISOString(),
          location: "The Biryanis",
        });
      }

      setRestaurantWithBranches(response);
    } else {
      console.error("Google Login failed:", response.error);
    }
  };
  useEffect(() => {
    getRestaurantDetails();
  }, []);

  const [cartLoaded, setCartLoaded] = useState(false);

  useEffect(() => {
    const stored = loadCartFromStorage();
    setCartItems(stored);
    setCartLoaded(true); // ← enable saving only AFTER loading

    // Load user from localStorage
    const storedUser = localStorage.getItem("auth_user");
    const token = localStorage.getItem("auth_token");

    if (!storedUser || !token || isTokenExpired(token)) {
      localStorage.removeItem("auth_user");
      localStorage.removeItem("auth_token");
      setUser(null);
      return;
    }
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser); // make sure parse is JSON.parse
        console.log("Logged in user:", parsedUser);

        setUser({
          id: parsedUser.id,
          name: parsedUser.name,
          email: parsedUser.email,
          createdDate: parsedUser.createdDate,
        });
      } catch (error) {
        console.error("Failed to parse auth_user from localStorage:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (cartLoaded) {
      saveCartToStorage(cartItems);
    }
  }, [cartItems, cartLoaded]);

  useEffect(() => {
    if (cartItems.length > 0) {
      saveCartToStorage(cartItems);
    } else {
      // If cart is empty, clear storage
      localStorage.removeItem(apiConfig.CART_STORAGE_KEY);
      localStorage.removeItem(apiConfig.CART_EXPIRY_KEY);
    }
  }, [cartItems]);

  const logout = () => {
    setUser(null);
    clearCart();
    clearLocalStorage();
  };

  const addToCart = (newItem: CartItem) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) =>
          item.id === newItem.id &&
          JSON.stringify(item.customizations) ===
            JSON.stringify(newItem.customizations) &&
          item.specialInstructions === newItem.specialInstructions,
      );

      if (existingItem) {
        return prevItems.map((item) =>
          item === existingItem
            ? { ...item, quantity: item.quantity + newItem.quantity }
            : item,
        );
      } else {
        return [...prevItems, newItem];
      }
    });
  };

  const updateCartQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item,
      ),
    );
  };

  const removeFromCart = (itemId: string) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem(apiConfig.CART_STORAGE_KEY);
    localStorage.removeItem(apiConfig.CART_EXPIRY_KEY);
  };

  const clearLocalStorage = () => {
    localStorage.removeItem("auth_user");
  };

  const contextValue: AppContextType = {
    user,
    restaurant,
    restaurantWithBranches,
    isLoggedIn,
    isDeliveryModeSelected,
    cartItems,
    orderMode,
    login,
    register,
    loginWithGoogle,
    continueAsGuest,
    logout,
    addToCart,
    getRestaurantDetails,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    setOrderMode,
    setDeliveryModeSelected,
  };

  return (
    <AppContext.Provider value={contextValue}>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route
              path="/checkout"
              element={
                isLoggedIn ? (
                  <Elements stripe={stripePromise}>
                    <CheckoutPage />
                  </Elements>
                ) : (
                  <Navigate to="/login?redirect=checkout" replace />
                )
              }
            />
            <Route
              path="/profile"
              element={
                isLoggedIn ? (
                  <ProfilePage />
                ) : (
                  <Navigate to="/login?redirect=profile" replace />
                )
              }
            />
            <Route
              path="/order-confirmation/:orderId"
              element={
                // isLoggedIn ? (
                <OrderConfirmationPage />
                // ) : (
                //   <Navigate to="/login" replace />
                // )
              }
            />
          </Routes>
          <ScrollUpButton />
        </div>
      </Router>
    </AppContext.Provider>
  );
}

export default App;
